import React, { useEffect, useState } from "react";
import UserAvatar from "../../common/UserAvatar/UserAvatar";
import { executeQuery } from "../../../graphql/client";
import { fetchEventImages } from "../../../api/eventImages";
import CaptainDetailModal from "./CaptainDetailModal";
import RecentPhotosModal from "./RecentPhotosModal";
import "./CaptainStoryStrip.css";

// Phase I. Story-style horizontal strip at the top of UserDashboard's
// events tab. First circle is "Recent Photos" — opens a modal with
// the 5 most recently uploaded event photos (a workaround until we
// can tag individual photos to specific captains). Remaining circles
// are admin-curated captains from socialCaptains.

const PUBLIC_CAPTAINS_QUERY = `
  query PublicCaptainsForStrip {
    publicCaptains {
      id
      displayName
      bio
      profileImgKey
      joinDate
      eventsHostedCount
      recentlyHostedEvents {
        id
        title
        date
      }
    }
  }
`;

const CaptainStoryStrip = () => {
  const [captains, setCaptains] = useState([]);
  const [recentPhotos, setRecentPhotos] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [openCaptain, setOpenCaptain] = useState(null);
  const [showPhotos, setShowPhotos] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      // Captains + photos in parallel — both are best-effort, the
      // strip still renders if one fails.
      const [capRes, photoRes] = await Promise.allSettled([
        executeQuery(PUBLIC_CAPTAINS_QUERY, {}),
        fetchEventImages(),
      ]);
      if (cancelled) return;
      if (capRes.status === "fulfilled") {
        setCaptains(capRes.value?.publicCaptains || []);
      }
      if (photoRes.status === "fulfilled") {
        // Already sorted newest-first by the eventImages handler;
        // take the first 5.
        setRecentPhotos((photoRes.value?.items || []).slice(0, 5));
      }
      setLoaded(true);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Don't render the strip until we know whether there's content.
  // No captains AND no photos → render nothing rather than an empty
  // bar.
  if (loaded && captains.length === 0 && recentPhotos.length === 0) {
    return null;
  }

  return (
    <>
      <div className="captain-strip" aria-label="Captains and recent photos">
        <div className="captain-strip__scroller">
          {recentPhotos.length > 0 && (
            <button
              type="button"
              className="captain-strip__item"
              onClick={() => setShowPhotos(true)}
              aria-label="Recent photos"
            >
              <span className="captain-strip__circle captain-strip__circle--photos">
                <span className="captain-strip__photo-collage">
                  {recentPhotos.slice(0, 4).map((p, idx) => (
                    <span
                      key={p.s3Key || idx}
                      className="captain-strip__photo-tile"
                      style={{
                        backgroundImage: `url(https://s3.amazonaws.com/social-images-public/${p.s3Key})`,
                      }}
                    />
                  ))}
                </span>
              </span>
              <span className="captain-strip__name">Recent photos</span>
            </button>
          )}

          {captains.map((c) => (
            <button
              type="button"
              key={c.id}
              className="captain-strip__item"
              onClick={() => setOpenCaptain(c)}
              aria-label={`View ${c.displayName}`}
            >
              <UserAvatar
                name={c.displayName}
                imgKey={c.profileImgKey}
                size={72}
                className="captain-strip__avatar"
              />
              <span className="captain-strip__name">{c.displayName}</span>
            </button>
          ))}
        </div>
      </div>

      {openCaptain && (
        <CaptainDetailModal
          captain={openCaptain}
          onClose={() => setOpenCaptain(null)}
        />
      )}

      {showPhotos && (
        <RecentPhotosModal
          photos={recentPhotos}
          onClose={() => setShowPhotos(false)}
        />
      )}
    </>
  );
};

export default CaptainStoryStrip;
